import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, formatPrice, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Car, Users, ArrowLeftRight, Plus, Edit, Trash2, LogOut, Upload, X, RefreshCw } from "lucide-react";

const empty = {
    merk: "", model: "", bouwjaar: 2022, kilometerstand: 0, brandstof: "Benzine",
    transmissie: "Handgeschakeld", prijs: 0, vermogen: 0, cilinderinhoud: 0,
    kleur: "", carrosserie: "", deuren: 5, apk_tot: "", beschrijving: "",
    opties: [], afbeeldingen: [],
};

export default function AdminDashboardPage() {
    const { user, ready, logout } = useAuth();
    const [tab, setTab] = useState("overzicht");
    const [stats, setStats] = useState({});
    const [cars, setCars] = useState([]);
    const [leads, setLeads] = useState([]);
    const [tradeins, setTradeins] = useState([]);
    const [editing, setEditing] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const load = async () => {
        const [s, c, l, t] = await Promise.all([
            api.get("/admin/stats"), api.get("/cars?limit=200"),
            api.get("/admin/leads"), api.get("/admin/tradeins"),
        ]);
        setStats(s.data); setCars(c.data); setLeads(l.data); setTradeins(t.data);
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/sync-2dehands");
            if (data.status === "ok") {
                toast.success(`Sync: +${data.added} nieuw · ~${data.updated} bijgewerkt · -${data.removed} verwijderd`);
                load();
            } else {
                toast.error(`Sync mislukt: ${data.error || data.status}`);
            }
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally { setSyncing(false); }
    };

    useEffect(() => { if (ready && user && user.role === "admin") load(); }, [ready, user]);

    if (ready && (!user || user.role !== "admin")) return <Navigate to="/admin/login" replace />;
    if (!ready) return <div className="min-h-screen flex items-center justify-center text-slate-500">Laden...</div>;

    const remove = async (id) => {
        if (!window.confirm("Auto verwijderen?")) return;
        try { await api.delete(`/admin/cars/${id}`); toast.success("Verwijderd"); load(); }
        catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            {/* Top bar */}
            <header className="bg-[#0E2C4D] text-white">
                <div className="ucs-container flex items-center justify-between h-16">
                    <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
                        <img src="/brand/logo.png" alt="Used Car Selection" className="h-12 w-auto"
                             style={{ filter: "brightness(0) invert(1)" }} />
                        <div>
                            <div className="text-xs text-white/60">Beheerportaal</div>
                            <div className="font-bold tracking-tight">Used Car Selection</div>
                        </div>
                    </Link>
                    <button onClick={logout} className="text-sm flex items-center gap-2 hover:text-[#EC6A0A]" data-testid="admin-logout">
                        <LogOut className="w-4 h-4" /> Uitloggen
                    </button>
                </div>
            </header>

            <div className="ucs-container py-8 grid md:grid-cols-12 gap-8">
                <aside className="md:col-span-3">
                    <nav className="bg-white border border-slate-200 p-2" style={{borderRadius: 2}}>
                        {[
                            { k: "overzicht", l: "Overzicht", icon: <LayoutDashboard className="w-4 h-4" /> },
                            { k: "voorraad", l: "Voorraad", icon: <Car className="w-4 h-4" /> },
                            { k: "leads", l: "Leads", icon: <Users className="w-4 h-4" /> },
                            { k: "inruil", l: "Inruil", icon: <ArrowLeftRight className="w-4 h-4" /> },
                        ].map((it) => (
                            <button key={it.k} onClick={() => setTab(it.k)}
                                    className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 ${tab === it.k ? "bg-[#0E2C4D] text-white" : "text-[#0E2C4D] hover:bg-slate-50"}`}
                                    style={{borderRadius: 2}} data-testid={`admin-tab-${it.k}`}>
                                {it.icon} {it.l}
                            </button>
                        ))}
                    </nav>
                </aside>

                <main className="md:col-span-9 space-y-6">
                    {tab === "overzicht" && (
                        <>
                            <h1 className="text-3xl font-black text-[#0E2C4D]">Overzicht</h1>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <StatCard label="Auto's" value={stats.auto_aantal || 0} testid="stat-cars" />
                                <StatCard label="Leads totaal" value={stats.leads_aantal || 0} testid="stat-leads" />
                                <StatCard label="Nieuwe leads" value={stats.leads_nieuw || 0} testid="stat-leads-new" />
                                <StatCard label="Inruilen" value={stats.tradeins_aantal || 0} testid="stat-tradeins" />
                            </div>

                            <div className="bg-white border border-slate-200 p-6 mt-6" style={{borderRadius: 2}} data-testid="sync-card">
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="label-eyebrow text-slate-500 mb-1">2dehands.be sync</div>
                                        <h3 className="text-lg font-bold text-[#0E2C4D]">Automatische voorraad-synchronisatie</h3>
                                        {stats.last_sync ? (
                                            <p className="text-sm text-slate-600 mt-1">
                                                Laatste sync: <strong>{new Date(stats.last_sync.ts).toLocaleString("nl-BE")}</strong>
                                                {" · "}
                                                {stats.last_sync.status === "ok" ? (
                                                    <>+{stats.last_sync.added} · ~{stats.last_sync.updated} · −{stats.last_sync.removed}</>
                                                ) : (
                                                    <span className="text-red-600">fout: {stats.last_sync.error}</span>
                                                )}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-slate-500 mt-1">Nog geen sync uitgevoerd.</p>
                                        )}
                                        <p className="text-xs text-slate-400 mt-2">Wordt automatisch elke 2 uur gesynchroniseerd.</p>
                                    </div>
                                    <button onClick={syncNow} disabled={syncing} className="ucs-btn-accent" data-testid="admin-sync-now">
                                        <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                                        {syncing ? "Bezig..." : "Sync nu"}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {tab === "voorraad" && (
                        editing ? (
                            <CarEditor initial={editing} onCancel={() => setEditing(null)}
                                       onSaved={() => { setEditing(null); load(); }} />
                        ) : (
                            <>
                                <div className="flex items-center justify-between">
                                    <h1 className="text-3xl font-black text-[#0E2C4D]">Voorraad</h1>
                                    <button onClick={() => setEditing({ ...empty })} className="ucs-btn-accent" data-testid="admin-add-car">
                                        <Plus className="w-4 h-4" /> Nieuwe auto
                                    </button>
                                </div>
                                <div className="bg-white border border-slate-200 overflow-hidden" style={{borderRadius: 2}}>
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-[#0E2C4D]">
                                            <tr>
                                                <th className="text-left p-4">Auto</th>
                                                <th className="text-left p-4">Bouwjaar</th>
                                                <th className="text-left p-4">Km</th>
                                                <th className="text-right p-4">Prijs</th>
                                                <th className="p-4"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cars.map((c) => (
                                                <tr key={c.id} className="border-t border-slate-100" data-testid={`admin-car-row-${c.id}`}>
                                                    <td className="p-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-14 h-10 bg-slate-100 overflow-hidden">
                                                                {c.afbeeldingen?.[0] && <img src={resolveImage(c.afbeeldingen[0])} alt="" className="w-full h-full object-cover" />}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-[#0E2C4D]">{c.merk} {c.model}</div>
                                                                <div className="text-xs text-slate-500">{c.brandstof} · {c.transmissie}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-4">{c.bouwjaar}</td>
                                                    <td className="p-4">{c.kilometerstand?.toLocaleString("nl-BE")}</td>
                                                    <td className="p-4 text-right font-bold">{formatPrice(c.prijs)}</td>
                                                    <td className="p-4 text-right whitespace-nowrap">
                                                        <button onClick={() => setEditing(c)} className="p-2 text-[#0E2C4D] hover:text-[#EC6A0A]" data-testid={`admin-edit-${c.id}`}><Edit className="w-4 h-4" /></button>
                                                        <button onClick={() => remove(c.id)} className="p-2 text-red-600 hover:text-red-800" data-testid={`admin-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {cars.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-slate-500">Geen auto's. Voeg er één toe.</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )
                    )}

                    {tab === "leads" && (
                        <>
                            <h1 className="text-3xl font-black text-[#0E2C4D]">Leads</h1>
                            <DataTable items={leads} columns={[
                                { k: "aangemaakt_op", l: "Datum", fmt: (v) => new Date(v).toLocaleString("nl-BE") },
                                { k: "type", l: "Type" }, { k: "naam", l: "Naam" }, { k: "email", l: "E-mail" },
                                { k: "telefoon", l: "Telefoon" }, { k: "bericht", l: "Bericht" },
                            ]} empty="Nog geen leads ontvangen." testid="leads-table" />
                        </>
                    )}

                    {tab === "inruil" && (
                        <>
                            <h1 className="text-3xl font-black text-[#0E2C4D]">Inruilaanvragen</h1>
                            <DataTable items={tradeins} columns={[
                                { k: "aangemaakt_op", l: "Datum", fmt: (v) => new Date(v).toLocaleString("nl-BE") },
                                { k: "naam", l: "Naam" }, { k: "telefoon", l: "Tel" },
                                { k: "merk", l: "Merk" }, { k: "model", l: "Model" },
                                { k: "bouwjaar", l: "Jaar" }, { k: "kilometerstand", l: "Km" },
                                { k: "versnellingsbak", l: "Bak" },
                                { k: "verwachte_prijs", l: "Verwacht €", fmt: (v) => v ? `€ ${Number(v).toLocaleString("nl-BE")}` : "—" },
                                { k: "fotos", l: "Foto's", fmt: (v) => v && v.length ? (
                                    <div className="flex gap-1">{v.slice(0,3).map((u, i) => <a key={i} href={resolveImage(u)} target="_blank" rel="noopener noreferrer"><img src={resolveImage(u)} alt="" className="w-10 h-10 object-cover border border-slate-200" /></a>)}{v.length>3 && <span className="text-xs text-slate-400">+{v.length-3}</span>}</div>
                                ) : "—" },
                            ]} empty="Geen inruilaanvragen." testid="tradeins-table" />
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

const StatCard = ({ label, value, testid }) => (
    <div className="bg-white border border-slate-200 p-6" style={{borderRadius: 2}} data-testid={testid}>
        <div className="label-eyebrow text-slate-500 mb-2">{label}</div>
        <div className="text-3xl font-black text-[#0E2C4D]">{value}</div>
    </div>
);

const DataTable = ({ items, columns, empty, testid }) => (
    <div className="bg-white border border-slate-200 overflow-x-auto" style={{borderRadius: 2}} data-testid={testid}>
        <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[#0E2C4D]">
                <tr>{columns.map((c) => <th key={c.k} className="text-left p-4">{c.l}</th>)}</tr>
            </thead>
            <tbody>
                {items.map((it) => (
                    <tr key={it.id} className="border-t border-slate-100">
                        {columns.map((c) => <td key={c.k} className="p-4 align-top">{c.fmt ? c.fmt(it[c.k]) : (it[c.k] || "—")}</td>)}
                    </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={columns.length} className="p-12 text-center text-slate-500">{empty}</td></tr>}
            </tbody>
        </table>
    </div>
);

const CarEditor = ({ initial, onCancel, onSaved }) => {
    const [f, setF] = useState({
        ...initial,
        opties: Array.isArray(initial.opties) ? initial.opties.join(", ") : "",
    });
    const [uploading, setUploading] = useState(false);
    const isEdit = !!initial.id;

    const set = (k, v) => setF({ ...f, [k]: v });

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/admin/upload", fd);
                urls.push(data.url);
            }
            setF({ ...f, afbeeldingen: [...(f.afbeeldingen || []), ...urls] });
            toast.success(`${urls.length} foto('s) geüpload`);
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setUploading(false); }
    };

    const removeImg = (i) => setF({ ...f, afbeeldingen: f.afbeeldingen.filter((_, j) => j !== i) });

    const save = async () => {
        try {
            const body = {
                ...f,
                opties: f.opties.split(",").map((s) => s.trim()).filter(Boolean),
                bouwjaar: parseInt(f.bouwjaar) || 0,
                kilometerstand: parseInt(f.kilometerstand) || 0,
                prijs: parseFloat(f.prijs) || 0,
                vermogen: parseInt(f.vermogen) || null,
                cilinderinhoud: parseInt(f.cilinderinhoud) || null,
                deuren: parseInt(f.deuren) || null,
            };
            if (isEdit) await api.put(`/admin/cars/${initial.id}`, body);
            else await api.post("/admin/cars", body);
            toast.success("Opgeslagen");
            onSaved();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    return (
        <div className="bg-white border border-slate-200 p-8" style={{borderRadius: 2}} data-testid="admin-car-editor">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-black text-[#0E2C4D]">{isEdit ? "Auto bewerken" : "Nieuwe auto"}</h1>
                <button onClick={onCancel} className="text-sm text-slate-500" data-testid="admin-editor-cancel">Annuleren</button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <Inp label="Merk" v={f.merk} on={(v) => set("merk", v)} testid="ce-merk" />
                <Inp label="Model" v={f.model} on={(v) => set("model", v)} testid="ce-model" />
                <Inp label="Bouwjaar" type="number" v={f.bouwjaar} on={(v) => set("bouwjaar", v)} testid="ce-bouwjaar" />
                <Inp label="Kilometerstand" type="number" v={f.kilometerstand} on={(v) => set("kilometerstand", v)} testid="ce-km" />
                <Sel label="Brandstof" v={f.brandstof} on={(v) => set("brandstof", v)} opts={["Benzine", "Diesel", "Elektrisch", "Hybride", "LPG"]} testid="ce-brandstof" />
                <Sel label="Transmissie" v={f.transmissie} on={(v) => set("transmissie", v)} opts={["Handgeschakeld", "Automaat"]} testid="ce-transmissie" />
                <Inp label="Prijs (€)" type="number" v={f.prijs} on={(v) => set("prijs", v)} testid="ce-prijs" />
                <Inp label="Vermogen (pk)" type="number" v={f.vermogen} on={(v) => set("vermogen", v)} testid="ce-vermogen" />
                <Inp label="Cilinderinhoud (cc)" type="number" v={f.cilinderinhoud} on={(v) => set("cilinderinhoud", v)} testid="ce-cc" />
                <Inp label="Kleur" v={f.kleur} on={(v) => set("kleur", v)} testid="ce-kleur" />
                <Inp label="Carrosserie" v={f.carrosserie} on={(v) => set("carrosserie", v)} testid="ce-carrosserie" />
                <Inp label="Deuren" type="number" v={f.deuren} on={(v) => set("deuren", v)} testid="ce-deuren" />
                <Inp label="APK tot (YYYY-MM)" v={f.apk_tot} on={(v) => set("apk_tot", v)} testid="ce-apk" />
                <Inp label="Opties (komma gescheiden)" v={f.opties} on={(v) => set("opties", v)} testid="ce-opties" full />
            </div>

            <div className="mt-4">
                <label className="block text-xs font-bold uppercase tracking-wider text-[#0E2C4D] mb-2">Beschrijving</label>
                <textarea rows={4} value={f.beschrijving} onChange={(e) => set("beschrijving", e.target.value)}
                          className="w-full border border-slate-300 px-4 py-3 text-sm" style={{borderRadius: 2}} data-testid="ce-beschrijving" />
            </div>

            <div className="mt-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-[#0E2C4D] mb-2">Afbeeldingen</label>
                <div className="flex flex-wrap gap-3 mb-3">
                    {(f.afbeeldingen || []).map((u, i) => (
                        <div key={i} className="relative w-28 h-20 bg-slate-100 overflow-hidden border border-slate-200">
                            <img src={resolveImage(u)} className="w-full h-full object-cover" alt="" />
                            <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-white/95 p-1" data-testid={`ce-remove-img-${i}`}>
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
                <label className="ucs-btn-primary inline-flex cursor-pointer">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploaden..." : "Foto's uploaden"}
                    <input type="file" multiple accept="image/*" className="hidden" onChange={upload} data-testid="ce-upload" />
                </label>
            </div>

            <div className="mt-8 flex gap-3">
                <button onClick={save} className="ucs-btn-accent" data-testid="ce-save">{isEdit ? "Opslaan" : "Aanmaken"}</button>
                <button onClick={onCancel} className="ucs-btn-primary">Annuleren</button>
            </div>
        </div>
    );
};

const Inp = ({ label, type = "text", v, on, full, testid }) => (
    <div className={full ? "sm:col-span-2" : ""}>
        <label className="block text-xs font-bold uppercase tracking-wider text-[#0E2C4D] mb-2">{label}</label>
        <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)}
               className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#EC6A0A]"
               style={{borderRadius: 2}} data-testid={testid} />
    </div>
);

const Sel = ({ label, v, on, opts, testid }) => (
    <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-[#0E2C4D] mb-2">{label}</label>
        <select value={v} onChange={(e) => on(e.target.value)} className="w-full border border-slate-300 px-4 py-3 text-sm bg-white" style={{borderRadius: 2}} data-testid={testid}>
            {opts.map((o) => <option key={o}>{o}</option>)}
        </select>
    </div>
);
